<section class="mx-auto w-full max-w-5xl px-4 py-6 md:px-8 md:py-10">
  @if (loading) {
    <article class="rounded-2xl border border-slate-700 bg-slate-900/60 p-8 text-center">
      <div class="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-2 border-amber-300 border-t-transparent"></div>
      <h1 class="text-xl font-semibold text-slate-100">Loading subscription</h1>
      <p class="mt-2 text-sm text-slate-300">Please wait a moment.</p>
    </article>
  } @else if (hasError) {
    <article class="rounded-2xl border border-red-500/40 bg-red-950/30 p-8 text-center">
      <h1 class="text-xl font-semibold text-red-200">Unable to load subscription</h1>
      <p class="mt-2 text-sm text-red-100/85">Try again in a few seconds.</p>
      <button
        type="button"
        class="clp-btn clp-btn-warning mt-5"
        (click)="reload()">
        Retry
      </button>
    </article>
  } @else if (notFound || !subscription) {
    <article class="rounded-2xl border border-slate-700 bg-slate-900/60 p-8 text-center">
      <h1 class="text-xl font-semibold text-slate-100">Subscription not found</h1>
      <p class="mt-2 text-sm text-slate-300">
        We could not find a subscription for <span class="font-semibold text-slate-100">{{ getPublicHandle() }}</span> using slug <span class="font-semibold text-slate-100">{{ slug }}</span>.
      </p>
      <a
        [routerLink]="['/']"
        class="clp-btn clp-btn-warning mt-5 inline-flex">
        Back to home
      </a>
    </article>
  } @else {
    <article class="overflow-hidden rounded-2xl border border-slate-700 bg-slate-900/60 shadow-lg">
      <div class="grid md:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
        <div class="relative min-h-64 bg-slate-950">
          @if (resolveCoverImageUrl(subscription.coverUrl); as coverUrl) {
            <img
              [src]="coverUrl"
              [alt]="subscription.name ?? 'Subscription cover'"
              class="h-full w-full object-cover" />
          } @else {
            <div class="flex h-full min-h-64 items-center justify-center text-slate-500">
              <i class="fa-solid fa-box-open text-4xl"></i>
            </div>
          }
        </div>

        <div class="p-6 md:p-8">
          <p class="text-xs uppercase tracking-wide text-slate-400">Subscription</p>
          <h1 class="mt-2 text-3xl font-semibold text-slate-100">{{ subscription.name }}</h1>
          <p class="mt-2 text-sm text-slate-300">By {{ getPublicHandle() }}</p>

          <div class="mt-4 flex flex-wrap gap-2">
            <span
              class="rounded-full border px-2.5 py-1 text-xs font-semibold"
              [ngClass]="getVisibilityBadgeClass(subscription.visibility)">
              {{ getVisibilityLabelKey(subscription.visibility) | translate }}
            </span>
            <span
              class="rounded-full border px-2.5 py-1 text-xs font-semibold"
              [ngClass]="getBillingTypeBadgeClass(subscription.billingType)">
              {{ getBillingTypeLabelKey(subscription.billingType) | translate }}
            </span>
          </div>

          <div class="mt-6 rounded-xl border border-slate-700 bg-slate-950/60 p-4">
            @if (isRecurringProduct(subscription)) {
              <p class="text-2xl font-semibold text-slate-100">
                {{ subscription.recurringFee ?? 0 | number:'1.2-2' }} {{ subscription.currency ?? 'USD' }}
              </p>
              <p class="mt-1 text-sm text-slate-300">
                Billed every {{ subscription.recurringIntervalCount ?? 1 }} {{ getRecurringIntervalLabelKey(subscription.recurringInterval) | translate | lowercase }}
              </p>
            } @else {
              <p class="text-2xl font-semibold text-slate-100">
                {{ subscription.oneTimeFee ?? 0 | number:'1.2-2' }} {{ subscription.currency ?? 'USD' }}
              </p>
              <p class="mt-1 text-sm text-slate-300">One-time purchase</p>
            }
          </div>

          @if (subscription.description) {
            <p class="mt-6 whitespace-pre-line text-sm leading-relaxed text-slate-200">
              {{ subscription.description }}
            </p>
          }

          <div class="mt-6 grid gap-3 text-sm text-slate-300 sm:grid-cols-2">
            <div class="rounded-lg border border-slate-700 bg-slate-950/40 p-3">
              <p class="text-xs uppercase tracking-wide text-slate-500">Slug</p>
              <p class="mt-1 font-medium text-slate-100">{{ subscription.slug ?? slug }}</p>
            </div>
            <div class="rounded-lg border border-slate-700 bg-slate-950/40 p-3">
              <p class="text-xs uppercase tracking-wide text-slate-500">Published At</p>
              <p class="mt-1 font-medium text-slate-100">{{ getPublishedDate(subscription.publishedAt) | date:'mediumDate' }}</p>
            </div>
          </div>
        </div>
      </div>
    </article>
  }
</section>
